import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { bousanmSolideApi } from '../services/bousanmSolideApi';
import { Folder, Users, DollarSign, TrendingUp, Award, Plus, Pencil, Trash2, Settings, BarChart2, Download, Search, CheckCircle2, Calculator, AlertCircle, Trash } from 'lucide-react';
import { useToast } from '../../../../hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";

const PlansBS = () => {
  const { t } = useTranslation(['solide', 'common']);
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('config'); // 'config' | 'stats'
  const [loading, setLoading] = useState(true);
  const [tauxChange, setTauxChange] = useState(() => parseFloat(localStorage.getItem('plans_taux_change')) || 130);

  // Taux Modal State
  const [isTauxModalOpen, setIsTauxModalOpen] = useState(false);
  const [tempTaux, setTempTaux] = useState(tauxChange);

  // Delete Confirmation State
  const [deleteId, setDeleteId] = useState(null);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);

  // Data
  const [stats, setStats] = useState({ plans: [], global: {} });
  const [plansConfig, setPlansConfig] = useState([]);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    nom: '',
    description: '',
    montant_hebdo_htg: 0,
    montant_hebdo_usd: 0,
    bonis_max_htg: 0,
    bonis_max_usd: 0,
    actif: true,
    ordre: 0
  });

  useEffect(() => {
    fetchData();
    fetchTaux();
  }, [activeTab]);

  const fetchTaux = async () => {
    try {
      const data = await bousanmSolideApi.getTauxChange();
      if (data && data.taux) {
        setTauxChange(data.taux);
        localStorage.setItem('plans_taux_change', data.taux);
      }
    } catch (error) {
      console.error("Erreur chargement taux:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'config') {
        const data = await bousanmSolideApi.getPlansConfiguration();
        setPlansConfig(data.plans || []);
      } else {
        const data = await bousanmSolideApi.getPlansStats();
        setStats(data);
      }
    } catch (error) {
      console.error("Erreur chargement données:", error);
      toast({
        title: t('solide:plansPage.messages.error'),
        description: t('common:errors.loadingFailed'),
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        code: plan.code,
        nom: plan.nom,
        description: plan.description || '',
        montant_hebdo_htg: plan.montant_hebdo_htg,
        montant_hebdo_usd: plan.montant_hebdo_usd,
        bonis_max_htg: plan.bonis_max_htg,
        bonis_max_usd: plan.bonis_max_usd,
        actif: plan.actif,
        ordre: plan.ordre
      });
    } else {
      setEditingPlan(null);
      setFormData({
        code: '',
        nom: '',
        description: '',
        montant_hebdo_htg: 0,
        montant_hebdo_usd: 0,
        bonis_max_htg: 0,
        bonis_max_usd: 0,
        actif: true,
        ordre: 0
      });
    }
    setIsModalOpen(true);
  };

  const handleConfigureTaux = async (e) => {
    e.preventDefault();
    if (!tempTaux || tempTaux <= 0) {
      toast({
        title: t('solide:plansPage.messages.error'),
        description: t('solide:plansPage.messages.invalidTaux'),
        type: "error"
      });
      return;
    }

    try {
      const taux = parseFloat(tempTaux);
      await bousanmSolideApi.updateTauxChange(taux);
      setTauxChange(taux);
      localStorage.setItem('plans_taux_change', taux);
      setIsTauxModalOpen(false);
      toast({
        title: t('solide:plansPage.messages.tauxUpdated'),
        description: t('solide:plansPage.messages.tauxUpdatedDesc', { taux }),
        type: "success"
      });
    } catch (error) {
      console.error("Erreur mise à jour taux:", error);
      toast({
        title: t('solide:plansPage.messages.error'),
        description: t('solide:plansPage.messages.tauxUpdateError'),
        type: "error"
      });
    }
  };

  const handleUsdChange = (val) => {
    const usd = parseFloat(val) || 0;
    const htg = usd * tauxChange;

    setFormData({
      ...formData,
      montant_hebdo_usd: usd,
      montant_hebdo_htg: htg, // Calcul auto : USD * Taux
      bonis_max_usd: usd * 4, // Calcul auto : USD * 4 semaines
      bonis_max_htg: htg * 4  // Calcul auto : HTG * 4 semaines
    });
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      if (editingPlan) {
        await bousanmSolideApi.updatePlan(editingPlan.id, formData);
        toast({ title: t('solide:plansPage.messages.success'), description: t('solide:plansPage.messages.saveSuccess'), type: "success" });
      } else {
        await bousanmSolideApi.createPlan(formData);
        toast({ title: t('solide:plansPage.messages.success'), description: t('solide:plansPage.messages.createSuccess'), type: "success" });
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      console.error("Erreur sauvegarde plan:", error);
      const message = error.response?.data?.message || 'Erreur lors de la sauvegarde du plan';
      toast({
        title: t('solide:plansPage.messages.error'),
        description: Array.isArray(message) ? message[0] : message,
        type: "error"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDeleteAlert = (plan) => {
    setPlanToDelete(plan);
    setIsDeleteAlertOpen(true);
  };

  const confirmDelete = async () => {
    if (!planToDelete) return;

    try {
      await bousanmSolideApi.deletePlan(planToDelete.id);
      toast({
        title: t('solide:plansPage.messages.deleteTitle'),
        description: t('solide:plansPage.messages.deleteSuccess'),
        type: "success"
      });
      fetchData();
    } catch (error) {
      console.error("Erreur suppression plan:", error);
      const message = error.response?.data?.error || 'Impossible de supprimer ce plan';
      toast({
        title: t('solide:plansPage.messages.error'),
        description: message,
        type: "error"
      });
    } finally {
      setIsDeleteAlertOpen(false);
      setPlanToDelete(null);
    }
  };

  const PLANS_CONFIG_VISUAL = {
    'ESPWA': { color: 'blue', desc: '52 semaines', icon: Folder },
    'RICHÈS': { color: 'purple', desc: 'Long terme', icon: Award },
    'KOURAJ': { color: 'orange', desc: 'Flexible', icon: TrendingUp },
    'SOUTENI': { color: 'green', desc: 'Solidaire', icon: Users },
    'AVNI': { color: 'indigo', desc: 'Futur', icon: TrendingUp },
  };

  return (
    <div className="h-full w-full bg-gray-50/50 overflow-y-auto p-4">
      {/* Header & Actions */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4 gap-4">
        <div>
          <h1 className="text-lg font-bold text-gray-900">{t('solide:plansPage.title')}</h1>
          <p className="text-xs text-gray-500">{t('solide:plansPage.subtitle')}</p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
          <div className="flex bg-white border border-gray-200 p-1 rounded-lg shadow-sm w-full sm:w-auto">
            <button
              onClick={() => setActiveTab('config')}
              className={`flex-1 sm:flex-none justify-center px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-2 ${activeTab === 'config' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
                }`}
            >
              <Settings className="h-3.5 w-3.5" />
              {t('solide:plansPage.tabs.config')}
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`flex-1 sm:flex-none justify-center px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-2 ${activeTab === 'stats' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
                }`}
            >
              <BarChart2 className="h-3.5 w-3.5" />
              {t('solide:plansPage.tabs.stats')}
            </button>
          </div>

          {activeTab === 'config' && (
            <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
              <Button onClick={() => { setTempTaux(tauxChange); setIsTauxModalOpen(true); }} variant="outline" className="h-9 px-3 gap-2 text-gray-600 text-xs border-dashed w-full sm:w-auto justify-center">
                <Calculator className="h-3.5 w-3.5" />
                <span className="truncate">{t('solide:plansPage.buttons.taux', { taux: tauxChange })}</span>
              </Button>
              <Button onClick={() => handleOpenModal()} className="h-9 px-4 gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs w-full sm:w-auto justify-center">
                <Plus className="h-3.5 w-3.5" />
                <span className="whitespace-nowrap">{t('solide:plansPage.buttons.newPlan')}</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : activeTab === 'config' ? (
        // VUE CONFIGURATION
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden flex flex-col">
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                <div className="grid grid-cols-12 gap-4 p-3 bg-gray-50/50 border-b text-xs font-medium text-gray-500">
                  <div className="col-span-1 pl-2">{t('solide:plansPage.table.order')}</div>
                  <div className="col-span-2">{t('solide:plansPage.table.code')}</div>
                  <div className="col-span-3">{t('solide:plansPage.table.nameDesc')}</div>
                  <div className="col-span-2 text-right">{t('solide:plansPage.table.weeklyHtg')}</div>
                  <div className="col-span-2 text-right">{t('solide:plansPage.table.weeklyUsd')}</div>
                  <div className="col-span-1 text-center">{t('solide:plansPage.table.status')}</div>
                  <div className="col-span-1"></div>
                </div>

                <div className="divide-y">
                  {plansConfig.map((plan) => (
                    <div key={plan.id} className="grid grid-cols-12 gap-4 p-3 items-center hover:bg-gray-50 transition-colors">
                      <div className="col-span-1 pl-2 text-gray-500 text-xs">{plan.ordre}</div>
                      <div className="col-span-2">
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 font-mono border border-gray-200">
                          {plan.code}
                        </span>
                      </div>
                      <div className="col-span-3">
                        <div className="text-sm font-medium text-gray-900">{plan.nom}</div>
                        <div className="text-[10px] text-gray-400 truncate">{plan.description}</div>
                      </div>
                      <div className="col-span-2 text-right font-medium text-sm text-gray-900">
                        {plan.montant_hebdo_htg?.toLocaleString()} <span className="text-[10px] text-gray-400 font-normal">G</span>
                      </div>
                      <div className="col-span-2 text-right font-medium text-sm text-gray-900">
                        {plan.montant_hebdo_usd?.toLocaleString()} <span className="text-[10px] text-gray-400 font-normal">$</span>
                      </div>
                      <div className="col-span-1 text-center">
                        <Badge className={`h-5 text-[10px] ${plan.actif ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-gray-100 text-gray-600 hover:bg-gray-100'}`}>
                          {plan.actif ? t('solide:plansPage.table.active') : t('solide:plansPage.table.inactive')}
                        </Badge>
                      </div>
                      <div className="col-span-1 flex justify-end gap-1 pr-2">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => handleOpenModal(plan)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleOpenDeleteAlert(plan)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {plansConfig.length === 0 && (
                    <div className="p-8 text-center text-gray-500 text-sm">
                      {t('solide:plansPage.table.empty')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // VUE STATISTIQUES
        <div className="space-y-6">
          {/* Global Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Card className="shadow-sm border-none bg-white"><CardContent className="p-4 flex items-center justify-between">
              <div><p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{t('solide:plansPage.stats.activeClients')}</p><div className="text-xl font-bold mt-0.5">{(stats.global?.clients || 0).toLocaleString()}</div></div>
              <div className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center"><Users className="h-4 w-4 text-blue-600" /></div>
            </CardContent></Card>
            <Card className="shadow-sm border-none bg-white"><CardContent className="p-4 flex items-center justify-between">
              <div><p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{t('solide:plansPage.stats.totalSavings')}</p><div className="text-xl font-bold mt-0.5">{(stats.global?.epargne || 0).toLocaleString()} <span className="text-xs font-normal text-gray-400">HTG</span></div></div>
              <div className="h-8 w-8 rounded-full bg-green-50 flex items-center justify-center"><DollarSign className="h-4 w-4 text-green-600" /></div>
            </CardContent></Card>
            <Card className="shadow-sm border-none bg-white"><CardContent className="p-4 flex items-center justify-between">
              <div><p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{t('solide:plansPage.stats.availablePlans')}</p><div className="text-xl font-bold mt-0.5">{stats.plans?.length || 0}</div></div>
              <div className="h-8 w-8 rounded-full bg-purple-50 flex items-center justify-center"><Award className="h-4 w-4 text-purple-600" /></div>
            </CardContent></Card>
          </div>

          {/* Plans Detail Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {stats.plans?.map((plan, index) => {
              const config = PLANS_CONFIG_VISUAL[plan.nom] || { color: 'gray', desc: 'Plan standard', icon: Folder };

              return (
                <div key={index} className="bg-white rounded-md border shadow-sm p-4 hover:shadow-md transition-shadow">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-base font-bold text-gray-900">{plan.nom}</h3>
                      <p className="text-xs text-gray-500">{config.desc}</p>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-${config.color}-50 text-${config.color}-700 border border-${config.color}-100`}>
                      {t('solide:plansPage.table.active')}
                    </span>
                  </div>

                  {/* Metrics Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <p className="text-[10px] text-blue-600 font-semibold uppercase mb-1">{t('solide:plansPage.stats.clients')}</p>
                      <p className="text-sm font-bold text-gray-900">{plan.statistiques.clients_actifs}</p>
                      <p className="text-[10px] text-gray-500 mt-1">{t('solide:plansPage.table.active')}</p>
                    </div>

                    <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                      <p className="text-[10px] text-green-600 font-semibold uppercase mb-1">{t('solide:plansPage.stats.savings')}</p>
                      <p className="text-sm font-bold text-gray-900">{(plan.statistiques.epargne_totale || 0).toLocaleString()}</p>
                      <p className="text-[10px] text-gray-500 mt-1">HTG Total</p>
                    </div>

                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-100 col-span-2">
                      <div className="flex justify-between items-end">
                        <div>
                          <p className="text-[10px] text-purple-600 font-semibold uppercase mb-1">{t('solide:plansPage.stats.bonusAverage')}</p>
                          <p className="text-sm font-bold text-gray-900">{(plan.statistiques.bonis_total || 0).toLocaleString()} HTG</p>
                          <p className="text-[10px] text-gray-500 mt-1">{t('solide:plansPage.stats.accumulatedBonus')}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-900">{(plan.statistiques.montant_hebdo_moyen || 0).toFixed(0)} HTG</p>
                          <p className="text-[10px] text-gray-500 mt-1">{t('solide:plansPage.stats.weeklyAverage')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal Création/Modification */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px] p-3 max-h-[85vh] overflow-y-auto">
          <DialogHeader className="mb-1">
            <DialogTitle className="text-sm">{editingPlan ? t('solide:plansPage.modal.editTitle') : t('solide:plansPage.modal.newTitle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="grid gap-2.5">
            {/* Identification */}
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-4 grid gap-1">
                <Label htmlFor="code" className="text-[10px] font-medium text-gray-500">Code</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="ESPWA"
                  required
                  disabled={!!editingPlan}
                  className="uppercase font-mono h-7 text-xs"
                />
              </div>
              <div className="col-span-8 grid gap-1">
                <Label htmlFor="nom" className="text-[10px] font-medium text-gray-500">{t('common:labels.name')}</Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="ex: Plan Espwa"
                  required
                  className="h-7 text-xs"
                />
              </div>
            </div>

            <div className="grid gap-1">
              <Label htmlFor="description" className="text-[10px] font-medium text-gray-500">{t('common:labels.description')}</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Courte description du plan"
                className="h-7 text-xs"
              />
            </div>

            {/* Montants */}
            <div className="bg-gray-50/50 rounded-md border p-2 mt-1">
              <div className="flex justify-between items-center mb-2">
                <p className="text-[10px] font-bold text-gray-900 uppercase flex items-center gap-2">
                  <Calculator className="h-3 w-3" /> {t('solide:plansPage.modal.financialConfig')}
                </p>
                <Badge variant="outline" className="text-[9px] h-4 px-1.5 bg-blue-50 text-blue-600 border-blue-200">
                  1 USD = {tauxChange} HTG
                </Badge>
              </div>

              {/* Saisie principale */}
              <div className="grid gap-1 mb-2">
                <Label htmlFor="montant_hebdo_usd" className="text-[10px] font-bold text-blue-700">{t('solide:plansPage.modal.weeklyUsd')}</Label>
                <div className="relative">
                  <Input
                    id="montant_hebdo_usd"
                    type="number"
                    value={formData.montant_hebdo_usd}
                    onChange={(e) => handleUsdChange(e.target.value)}
                    required
                    min="0"
                    className="h-8 text-sm font-bold border-blue-300 focus:border-blue-500 focus:ring-blue-500 pl-2 pr-8"
                    autoFocus
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">$</div>
                </div>
              </div>

              {/* Calculs Auto */}
              <div className="grid grid-cols-3 gap-2">
                <div className="grid gap-1">
                  <Label className="text-[9px] text-gray-500">{t('solide:plansPage.modal.weeklyHtg')}</Label>
                  <div className="h-7 bg-white border rounded px-2 flex items-center text-xs font-medium text-gray-700">
                    {formData.montant_hebdo_htg?.toLocaleString()} <span className="text-[9px] text-gray-400 ml-1">G</span>
                  </div>
                </div>

                <div className="grid gap-1">
                  <Label className="text-[9px] text-gray-500">{t('solide:plansPage.modal.maxBonusUsd')}</Label>
                  <div className="h-7 bg-white border rounded px-2 flex items-center text-xs font-medium text-gray-700">
                    {formData.bonis_max_usd?.toLocaleString()} <span className="text-[9px] text-gray-400 ml-1">$</span>
                  </div>
                </div>

                <div className="grid gap-1">
                  <Label className="text-[9px] text-gray-500">{t('solide:plansPage.modal.maxBonusHtg')}</Label>
                  <div className="h-7 bg-white border rounded px-2 flex items-center text-xs font-medium text-gray-700">
                    {formData.bonis_max_htg?.toLocaleString()} <span className="text-[9px] text-gray-400 ml-1">G</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center pt-2 border-t">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="actif"
                  checked={formData.actif}
                  onChange={(e) => setFormData({ ...formData, actif: e.target.checked })}
                  className="h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                />
                <Label htmlFor="actif" className="cursor-pointer text-xs text-gray-900 font-medium">{t('solide:plansPage.modal.activePlan')}</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Label htmlFor="ordre" className="text-[10px] text-gray-500">{t('solide:plansPage.modal.order')}</Label>
                <Input
                  id="ordre"
                  type="number"
                  value={formData.ordre}
                  onChange={(e) => setFormData({ ...formData, ordre: parseInt(e.target.value) })}
                  className="h-6 w-12 text-center text-xs"
                  min="0"
                />
              </div>
            </div>

            <DialogFooter className="mt-1">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="h-7 text-xs" disabled={submitting}>
                {t('solide:plansPage.modal.cancel')}
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 h-7 text-xs" disabled={submitting}>
                {submitting ? t('solide:plansPage.modal.waiting') : (editingPlan ? t('solide:plansPage.modal.save') : t('solide:plansPage.modal.create'))}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* Modal Taux */}
      <Dialog open={isTauxModalOpen} onOpenChange={setIsTauxModalOpen}>
        <DialogContent className="sm:max-w-[400px] p-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-4 w-4 text-blue-600" />
              {t('solide:plansPage.tauxModal.title')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newTaux" className="text-sm font-medium">{t('solide:plansPage.tauxModal.label')}</Label>
              <div className="relative">
                <Input
                  id="newTaux"
                  type="number"
                  value={tempTaux}
                  onChange={(e) => setTempTaux(e.target.value)}
                  className="pl-2 pr-8 font-bold text-lg h-12"
                  placeholder="ex: 130"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-400">HTG</div>
              </div>
              <p className="text-xs text-gray-500 italic">
                {t('solide:plansPage.tauxModal.hint')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTauxModalOpen(false)} className="h-9">
              {t('solide:plansPage.modal.cancel')}
            </Button>
            <Button onClick={handleConfigureTaux} className="bg-blue-600 hover:bg-blue-700 h-9">
              {t('solide:plansPage.modal.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Suppression */}
      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-2">
              <Trash className="h-6 w-6 text-red-600" />
            </div>
            <AlertDialogTitle className="text-center">{t('solide:plansPage.deleteAlert.title')}</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              {t('solide:plansPage.deleteAlert.description', { name: planToDelete?.nom })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center gap-2">
            <AlertDialogCancel className="mt-0">{t('solide:plansPage.deleteAlert.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              {t('solide:plansPage.deleteAlert.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PlansBS;
